import { ActionContext } from 'vuex';
import { DeviceService } from '@/services/DeviceService';

interface DeviceStateShape {
  // we don't need full typing of state here, actions only use commits
}

type Ctx = ActionContext<DeviceStateShape, any>;

export default {
  async loadDeviceData(context: Ctx, payload: { type: string; regId: string; devId: string; startTime: number; endTime: number; timeslot: number }) {
    try {
      const { timestamps, series } = await DeviceService.getTelemetryHistory({
        type: payload.type,
        registryId: payload.regId,
        deviceId: payload.devId,
        startTime: payload.startTime,
        endTime: payload.endTime,
      });

      const deviceData = {
        type: payload.type,
        data: timestamps.map((t, i) => ({ timestamp: t, value: series[i] }))
      };

      context.commit('setActiveDevice', { registryId: payload.regId, deviceId: payload.devId });
      context.commit('setTimeRange', payload.timeslot);
      context.commit('setDataType', payload.type);
      context.commit('setDataForType', deviceData);
    } catch (error: any) {
      throw new Error(error?.message || 'Failed to load device data');
    }
  },

  cleanData(context: Ctx){
    context.commit('resetData');
  },

  async forceRemoveDevice(context: Ctx, payload: { registryId: string; deviceId: string }) {
    const data = await DeviceService.forceRemoveDevice(payload.registryId, payload.deviceId);
    context.commit('setRemoveForceResponse', data);
  },

  async getDeviceInfo(context: Ctx, payload: { registryId: string; deviceId: string }) {
    const data = await DeviceService.getDeviceInfo(payload.registryId, payload.deviceId);
    context.commit('setResponseOnDeviceInfo', { data, status: 200 });
  },

  async getJoinState(context: Ctx, payload: { registryId: string; deviceId: string }) {
    const data = await DeviceService.getJoinState(payload.registryId, payload.deviceId);
    context.commit('setResponseOnJoinState', { data, status: 200 });
  },

  async getGatewayInfo(context: Ctx, payload: { registryId: string }) {
    const data = await DeviceService.getGatewayInfo(payload.registryId);
    context.commit('setResponseOnGatewayInfo', { data, status: 200 });
  },

  async getDeviceSettings(context: Ctx, payload: { registryId: string; deviceId: string }) {
    const data = await DeviceService.getDeviceSettings(payload.registryId, payload.deviceId);
    context.commit('setResponseOnDeviceSettings', { data, status: 200 });
  },

  async alarmsHistory(context: Ctx, payload: string) {
    // keep backward-compat signature that receives a query string
    const queryParams = payload.split('?type=')[1];
    const type = queryParams.split('&registryId=')[0];
    const registryIdAndRest = queryParams.split('&registryId=')[1];
    const registryId = registryIdAndRest.split('&deviceId=')[0];
    const deviceIdAndRest = registryIdAndRest.split('&deviceId=')[1];
    const deviceId = deviceIdAndRest.split('&startTime=')[0];
    const startTimeAndRest = deviceIdAndRest.split('&startTime=')[1];
    const startTime = startTimeAndRest.split('&endTime=')[0];
    const endTime = startTimeAndRest.split('&endTime=')[1];

    const { timestamps, series } = await DeviceService.getTelemetryHistory({ type, registryId, deviceId, startTime, endTime });
    context.commit('setResponseOnAlarmsHistory', { data: { timestamps, series }, status: 200 });
  },

  async telemetryHistory(context: Ctx, payload: string) {
    const queryParams = payload.split('?type=')[1];
    const type = queryParams.split('&registryId=')[0];
    const registryIdAndRest = queryParams.split('&registryId=')[1];
    const registryId = registryIdAndRest.split('&deviceId=')[0];
    const deviceIdAndRest = registryIdAndRest.split('&deviceId=')[1];
    const deviceId = deviceIdAndRest.split('&startTime=')[0];
    const startTimeAndRest = deviceIdAndRest.split('&startTime=')[1];
    const startTime = startTimeAndRest.split('&endTime=')[0];
    const endTime = startTimeAndRest.split('&endTime=')[1];

    const { timestamps, series } = await DeviceService.getTelemetryHistory({ type, registryId, deviceId, startTime, endTime });
    context.commit('setResponseOnTelemetryHistory', { data: { timestamps, series }, status: 200 });
  },

  async alarmSeen(context: Ctx, payload: { registryId: string; deviceId: string; alarmId: string; alarmType: string }) {
    const data = await DeviceService.alarmSeen(payload.registryId, payload.deviceId, payload.alarmId, payload.alarmType);
    context.commit('setResponseOnAlarmSeen', { data, status: 200 });
  },

  async alarmDismissed(context: Ctx, payload: { registryId: string; deviceId: string; alarmId: string; alarmType: string }) {
    const data = await DeviceService.alarmDismissed(payload.registryId, payload.deviceId, payload.alarmId, payload.alarmType);
    context.commit('setResponseOnAlarmDismissed', { data, status: 200 });
  },

  async readBorderRouterData(context: Ctx, payload: { gatewayId: string }) {
    const data = await DeviceService.readBorderRouterData(payload.gatewayId);
    context.commit('setResponseOnReadBorderRouterData', { data, status: 200 });
  },

  async readRoomSensorData(context: Ctx, payload: { deviceId: string }) {
    const data = await DeviceService.readRoomSensorData(payload.deviceId);
    context.commit('setResponseOnReadRoomSensorData', { data, status: 200 });
  },

  async getAlarmsHistory(context: Ctx, payload: { registryId: string; deviceId: string; startTime: number | string; endTime: number | string }) {
    const data = await DeviceService.getAlarmsHistory(payload.registryId, payload.deviceId, payload.startTime, payload.endTime);
    context.commit('setResponseOnAlarmsHistory', { data, status: 200 });
  },
};
